import { observable, runInAction } from 'mobx'
import { configurePersistable } from 'mobx-persist-store'
import { PERSISTABLE_CONFIG, PERSISTABLE_REACTION_CONFIG, STORES } from '@/constants/stores.js'
import { isAsyncFn } from '@/utils/function.js'
import { VIEWS } from '@/constants/views.js'
import { REPOSITORIES } from '@/constants/repositories.js'

const rootStore = new (class RootStore {
  #registeredAfterConstructors = []
  #registeredDeconstructors = []

  repositories = {}
  views = {}

  @observable accessor isInitializing = false
  @observable accessor isInitialized = false

  constructor() {
    const isNotProdEnv = import.meta.env.VITE_APP_ENV !== 'prod'
    if (isNotProdEnv) window.rootStore = this

    configurePersistable(PERSISTABLE_CONFIG, PERSISTABLE_REACTION_CONFIG)

    STORES.forEach(([id, store]) => {
      this[id] = new store(this)
      if (isNotProdEnv) window[id] = this[id]
    })

    REPOSITORIES.forEach(([id, repo]) => {
      this.repositories[id] = new repo(this)
      if (isNotProdEnv) window[id] = this.repositories[id]
    })

    VIEWS.forEach(([id, view]) => {
      this.views[id] = new view(this)
      if (isNotProdEnv) window[id] = this.views[id]
    })
  }

  init = async () => {
    if (this.isInitializing || this.isInitialized) return

    runInAction(() => {
      this.isInitializing = true
    })

    await this.#invokeMethods(this.#registeredAfterConstructors)

    runInAction(() => {
      this.isInitializing = false
      this.isInitialized = true
    })
  }

  reset = async () => {
    if (this.isInitializing || !this.isInitialized) return

    await this.#invokeMethods(this.#registeredDeconstructors)

    runInAction(() => {
      this.isInitializing = false
      this.isInitialized = false
    })
  }

  registerAfterConstructor = x => {
    if (this.isInitializing || this.isInitialized) return

    this.#registeredAfterConstructors.push(x)
  }

  registerDeconstructor = x => {
    if (this.isInitializing || this.isInitialized) return

    this.#registeredDeconstructors.push(x)
  }

  #invokeMethods = async methods => {
    const promises = methods.reduce((acc, x) => {
      if (typeof x !== 'function') return acc

      if (isAsyncFn(x)) acc.push(x())
      else x()

      return acc
    }, [])

    return Promise.all(promises)
  }
})()

export default rootStore
